import i18n from "@/shared/i18n/i18n"
import { Keyboard } from "@capacitor/keyboard"
import { alertController, isPlatform, modalController, toastController } from "@ionic/vue"
import {
    alertCircleOutline,
    alertOutline,
    checkmarkOutline,
    closeOutline,
    hourglassOutline,
    informationOutline
} from "ionicons/icons"
import { useHaptics } from "../lib/haptics"

// Обработчик нажатий клавиш Escape и Enter на клавиатуре
const handleAlertKeyDown = (event: KeyboardEvent) => {
    // Получаем элемент ion-alert
    const alertElement = document.querySelector("ion-alert")
    if (alertElement) {
        // Находим кнопки внутри alert
        const buttons = alertElement.querySelectorAll<HTMLButtonElement>(".alert-button")

        buttons.forEach((button) => {
            const key = button.getAttribute("data-key")
            if (key === event.key) {
                button.click() // Нажимаем соответствующую кнопку
            }
        })
    }
}
const handleToastKeyDown = (event: KeyboardEvent) => {
    // Получаем элемент ion-toast
    const toastElement = document.querySelector("ion-toast")
    if (toastElement && toastElement.shadowRoot) {
        // Находим кнопки внутри toast
        const buttons = toastElement.shadowRoot.querySelectorAll<HTMLButtonElement>(".toast-button")

        buttons.forEach((button) => {
            const key = button.getAttribute("data-key")
            if (key === event.key) {
                button.click() // Нажимаем соответствующую кнопку
            }
        })
    }
}

export function useInterface() {
    async function showNotification(
        text: string,
        type?: string,
        duration = 5000
    ): Promise<void> {
        const { feelNotification } = useHaptics()
        try {
            if (!text)
                throw new Error("Missing param")

            // const id = `toast-${Math.trunc(Date.now() / 1000)}`
            const toast = await toastController
                .create({
                    color: type,
                    message: text,
                    duration: duration,
                    position: "top",
                    cssClass: "custom",
                    swipeGesture: "vertical",
                    translucent: true,
                    animated: true,
                    icon: (type === "danger")
                        ? alertOutline
                        : (type === "warning")
                            ? alertCircleOutline
                            : (type === "success")
                                ? checkmarkOutline
                                : informationOutline,
                    buttons: [
                        { icon: closeOutline, role: "cancel", htmlAttributes: { "data-key": "Escape" } }
                    ]
                })

            // Удаление обработчика до создания тоста
            document.addEventListener("keydown", handleToastKeyDown)

            // Удаление обработчика после закрытия тоста
            toast.onDidDismiss().then(() => {
                document.removeEventListener("keydown", handleToastKeyDown)
            })

            await toast.present()

            if (type === "success" || type === "warning" || type === "danger")
                await feelNotification(type)
        }
        catch (error) {
            console.log(`Interface | showNotification: ${error}`)
        }
    }

    async function showControllableNotification(
        text: string,
        type?: string,
        duration = 500000000
    ): Promise<HTMLIonToastElement | null> {
        const { feelNotification } = useHaptics()
        try {
            if (!text)
                throw new Error("Missing param")

            // const id = `toast-${Math.trunc(Date.now() / 1000)}`
            const toast = await toastController
                .create({
                    color: type,
                    message: text,
                    duration: duration,
                    position: "top",
                    cssClass: "custom toast-spinner",
                    swipeGesture: "vertical",
                    translucent: true,
                    animated: true,
                    icon: hourglassOutline,
                    buttons: [
                        { icon: "assets/icon/times.svg" }
                    ]
                })

            await toast.present()
            if (type === "success" || type === "warning" || type === "danger")
                await feelNotification(type)
            return toast
        }
        catch (error) {
            console.log(`Interface | showControllableNotification: ${error}`)
            return null
        }
    }

    async function showNotificationWithButton(
        text: string,
        buttonText?: string,
        buttonFunc?: () => void,
        type?: string,
        duration = 10000
    ): Promise<void> {
        const { feelNotification } = useHaptics()
        try {
            if (!text)
                throw new Error("Missing param")

            const toastWithButton = await toastController
                .create({
                    color: type,
                    message: text,
                    duration: duration,
                    position: "top",
                    cssClass: "custom is-buttons",
                    layout: "stacked",
                    swipeGesture: "vertical",
                    icon: (type === "danger")
                        ? alertOutline
                        : (type === "warning")
                            ? alertCircleOutline
                            : (type === "success")
                                ? checkmarkOutline
                                : informationOutline,
                    buttons: [
                        {
                            text: (buttonText) ? buttonText : "Ok",
                            handler: (buttonFunc) ? buttonFunc : () => { console.log("") },
                            htmlAttributes: { "data-key": "Enter" }
                        },
                        { text: i18n.global.t("Interface.Button.Cancel"), role: "cancel", htmlAttributes: { "data-key": "Escape" } }
                    ]
                })

            // Удаление обработчика до создания тоста
            document.addEventListener("keydown", handleToastKeyDown)

            // Удаление обработчика после закрытия тоста
            toastWithButton.onDidDismiss().then(() => {
                document.removeEventListener("keydown", handleToastKeyDown)
            })

            await toastWithButton.present()

            if (type === "success" || type === "warning" || type === "danger")
                await feelNotification(type)
        }
        catch (error) {
            console.log(`Interface | showNotificationWithButton: ${error}`)
        }
    }

    async function showNotificationCompactWithButton(
        text: string,
        buttonText?: string,
        buttonFunc?: () => void,
        type?: string,
        duration = 10000
    ): Promise<void> {
        const { feelNotification } = useHaptics()
        try {
            if (!text)
                throw new Error("Missing param")

            const toastWithButton = await toastController
                .create({
                    color: type,
                    message: text,
                    duration: duration,
                    position: "top",
                    swipeGesture: "vertical",
                    cssClass: "custom is-buttons is-compact",
                    buttons: [
                        {
                            text: (buttonText) ? buttonText : "➔",
                            handler: (buttonFunc) ? buttonFunc : () => { console.log("") },
                            htmlAttributes: { "data-key": "Enter" }
                        }
                    ]
                })

            // Удаление обработчика до создания тоста
            document.addEventListener("keydown", handleToastKeyDown)

            // Удаление обработчика после закрытия тоста
            toastWithButton.onDidDismiss().then(() => {
                document.removeEventListener("keydown", handleToastKeyDown)
            })

            await toastWithButton.present()

            if (type === "success" || type === "warning" || type === "danger")
                await feelNotification(type)
        }
        catch (error) {
            console.log(`Interface | showNotificationWithButton: ${error}`)
        }
    }

    async function showAlert(
        headerText: string,
        messageText: string,
        buttonText?: string,
        buttonFunc?: () => void
    ): Promise<void> {
        const { feelImpact } = useHaptics()
        try {
            if (!headerText || !messageText)
                throw new Error("Missing params")

            const alert = await alertController
                .create({
                    header: headerText,
                    message: messageText,
                    cssClass: "custom",
                    buttons: [{
                        text: (buttonText) ? buttonText : "Ok",
                        htmlAttributes: { "data-key": "Enter" },
                        handler: (buttonFunc) ? buttonFunc : () => { console.log("") }
                    }]
                })
            // Удаление обработчика до создания алерта
            document.addEventListener("keydown", handleAlertKeyDown)

            // Удаление обработчика после закрытия алерта
            alert.onDidDismiss().then(() => {
                document.removeEventListener("keydown", handleAlertKeyDown)
            })

            await alert.present()

            await feelImpact("light")
        }
        catch (error) {
            console.log(`Interface | showAlert: ${error}`)
        }
    }

    async function showConfirm(
        headerText: string,
        messageText: string,
        cancelText: string,
        confirmText: string
    ): Promise<boolean> {
        const { feelNotification } = useHaptics()
        try {
            if (!headerText || !messageText || !cancelText || !confirmText)
                throw new Error("Missing params")

            const promt = await alertController.create({
                header: headerText,
                message: messageText,
                cssClass: "custom",
                buttons: [
                    {
                        text: cancelText,
                        role: "cancel"
                    },
                    {
                        text: confirmText,
                        role: "confirm",
                        htmlAttributes: { "data-key": "Enter" }
                    }
                ]
            })

            // Удаление обработчика до создания алерта
            document.addEventListener("keydown", handleAlertKeyDown)

            // Удаление обработчика после закрытия алерта
            promt.onDidDismiss().then(() => {
                document.removeEventListener("keydown", handleAlertKeyDown)
            })

            await promt.present()

            await feelNotification("warning")

            const { role } = await promt.onDidDismiss()
            // console.log(`Dismissed with role: ${role}`)

            if (role === "confirm")
                return true
            else
                return false
        }
        catch (error) {
            console.log(`Interface | showConfirmDialog: ${error}`)
            return false
        }
    }

    async function showPromt(
        header: string,
        message: string,
        inputPlaceholder: string,
        inputType: string,
        inputId: string,
        buttonText: string,
        buttonCallback: (inputText: string) => void,
        cancelCallback?: () => void
    ) {
        const { feelNotification } = useHaptics()

        const alert = await alertController.create({
            header,
            message,
            cssClass: "custom",
            buttons: [
                {
                    text: i18n.global.t("Interface.Button.Cancel"),
                    role: "cancel",
                    handler: async() => {
                        (cancelCallback) ? cancelCallback() : () => { }
                    }
                },
                {
                    text: buttonText,
                    role: "confirm",
                    htmlAttributes: { "data-key": "Enter" },
                    handler: async(inputs) => {
                        if (inputs && inputs[0] && inputs[0] !== "")
                            buttonCallback(inputs[0])
                    }
                }
            ],
            inputs: [
                {
                    id: inputId,
                    placeholder: inputPlaceholder,
                    attributes: {
                        type: inputType,
                        autofocus: true
                    }
                }
            ]
        })

        // Удаление обработчика до создания алерта
        document.addEventListener("keydown", handleAlertKeyDown)

        // Удаление обработчика после закрытия алерта
        alert.onDidDismiss().then(() => {
            document.removeEventListener("keydown", handleAlertKeyDown)
        })

        await alert.present()

        await feelNotification("warning")

        const passwordInputEl = document.getElementById(inputId) as HTMLElement
        passwordInputEl.focus()
        if (isPlatform("capacitor"))
            Keyboard.show()
    }

    async function showModal(component: any, props?: Record<string, unknown>) {
        const { feelImpact } = useHaptics()

        const el = document.getElementById("ionRouterOutlet") as HTMLElement
        const modal = await modalController
            .create({
                component,
                presentingElement: el,
                canDismiss: true,
                componentProps: { ...(props || {}) }
            })

        await feelImpact("light")
        return modal.present()
    }

    return {
        showNotification,
        showControllableNotification,
        showNotificationWithButton,
        showNotificationCompactWithButton,
        showAlert,
        showConfirm,
        showPromt,
        showModal
    }
}
